import { Popover as ArkPopover, Portal } from '@ark-ui/react'

/**
 * A small panel that opens from a word or a number, and lands somewhere it fits.
 *
 * **This was hand-written and should not have been.** The first version computed its own placement:
 * measuring the trigger, clamping to the viewport, flipping above the fold when the panel would run
 * off the bottom, wiring `pointerdown` and Escape listeners, hiding the first frame until the height
 * was known. All of it real problems, all of it solved — by Ark UI, which this project already
 * depends on and already uses for the tab strips, and which sits on Floating UI underneath.
 *
 * What we get by deleting that code rather than maintaining it:
 *
 * - **Collision handling that actually measures.** Flip and shift against the real boundary, not a
 *   clamp against `window.innerWidth` that a scrolling ancestor can still falsify.
 * - **Focus management.** Focus moves into the panel and returns to the trigger on close, which the
 *   hand-rolled version only did for Escape and not for an outside click.
 * - **Correct ARIA wiring**, including `aria-controls` and the id relationships between trigger,
 *   title and description — which the hand-rolled version simply did not have.
 * - **A portal**, so a trigger inside a horizontally scrolling table cannot have its panel clipped.
 *   `position: fixed` was the manual workaround for the same thing.
 *
 * `unmountOnExit` keeps the closed panels out of the DOM. That matters here beyond tidiness: the
 * ledger renders a `Detail` per number on two layouts, so leaving them mounted would put several
 * hundred hidden dialogs on the page.
 *
 * `lazyMount` is deliberately paired with it so the content is not built until first open.
 */
export function Popover({
  label,
  trigger,
  triggerClassName = '',
  children,
}: {
  /** Announced to a screen reader as the name of both the control and the panel. */
  label: string
  trigger: React.ReactNode
  triggerClassName?: string
  children: React.ReactNode
}) {
  return (
    <ArkPopover.Root
      lazyMount
      unmountOnExit
      // Below the trigger by preference; Ark flips and shifts it when that would not fit.
      positioning={{ placement: 'bottom-start', gutter: 4, overflowPadding: 8 }}
    >
      <ArkPopover.Trigger
        aria-label={label}
        // align-baseline is load-bearing, not tidiness: a button is inline-block, so without it the
        // trigger sits a hair off the baseline of its line — visible as a wobble once a paragraph or
        // a table row carries two. The rest undoes button chrome so it matches what surrounds it.
        className={`cursor-help bg-transparent p-0 text-left align-baseline font-[inherit] text-[inherit] leading-[inherit] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent ${triggerClassName}`}
      >
        {trigger}
      </ArkPopover.Trigger>
      <Portal>
        <ArkPopover.Positioner>
          <ArkPopover.Content className="z-50 max-h-[60vh] w-72 max-w-[calc(100vw-1rem)] overflow-y-auto overscroll-contain rounded-lg border border-line bg-surface-overlay p-3 text-left font-normal normal-case shadow-overlay focus-visible:outline-2 focus-visible:outline-accent">
            {/* Ark points the dialog's `aria-labelledby` at its Title, so one has to exist or the
                reference dangles and the panel announces as unnamed. It is visually hidden because
                callers render their own heading — a screen reader gets the fuller phrasing here
                ("Edge — what does this mean?") than the word shown on screen. */}
            <ArkPopover.Title className="sr-only">{label}</ArkPopover.Title>
            {children}
          </ArkPopover.Content>
        </ArkPopover.Positioner>
      </Portal>
    </ArkPopover.Root>
  )
}
